// Central SEO configuration for Zouq AI
// SITE_URL is the canonical production domain used in:
//   - <link rel="canonical">
//   - Open Graph og:url
//   - Schema.org @id and image URLs
//   - sitemap.xml / robots.txt / llms.txt
//
// We hard-code the production domain (zouq.ai) so search engines and AI
// crawlers always see one canonical address regardless of where the app is
// hosted during development. Update this single constant when the domain
// changes or when an additional domain is added.
export const SITE_URL = 'https://zouq.ai';

export const SITE = {
  url: SITE_URL,
  brand: 'Zouq AI',
  brandAr: 'ذوق AI',
  defaultLocale: 'ar_SA',
  alternateLocales: ['ar_SA', 'ar_AE', 'ar_KW', 'ar_BH', 'ar_QA', 'ar_OM', 'en_US'],
  twitter: '@ZouqAI',
  ogImage: `${SITE_URL}/og-image.png`,
  ogImageAr: `${SITE_URL}/og-image-ar.png`,
  themeColor: '#D97706',
  founded: '2025',
  countries: ['SA', 'AE', 'KW', 'BH', 'QA', 'OM'],
  languages: ['ar', 'en'],
};

// GCC market metadata used in Schema.org and content
export const GCC_MARKETS = [
  { code: 'SA', nameAr: 'المملكة العربية السعودية', nameEn: 'Saudi Arabia', currency: 'SAR', cities: ['الرياض', 'جدة', 'الدمام', 'مكة', 'المدينة', 'الخبر', 'الطائف'] },
  { code: 'AE', nameAr: 'الإمارات العربية المتحدة', nameEn: 'United Arab Emirates', currency: 'AED', cities: ['دبي', 'أبوظبي', 'الشارقة', 'العين', 'عجمان'] },
  { code: 'KW', nameAr: 'الكويت', nameEn: 'Kuwait', currency: 'KWD', cities: ['مدينة الكويت', 'حولي', 'السالمية'] },
  { code: 'BH', nameAr: 'البحرين', nameEn: 'Bahrain', currency: 'BHD', cities: ['المنامة', 'المحرق', 'الرفاع'] },
  { code: 'QA', nameAr: 'قطر', nameEn: 'Qatar', currency: 'QAR', cities: ['الدوحة', 'الريان', 'الوكرة'] },
  { code: 'OM', nameAr: 'عُمان', nameEn: 'Oman', currency: 'OMR', cities: ['مسقط', 'صلالة', 'صحار'] },
];

// Organization Schema.org data (used across all pages)
export const ORGANIZATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${SITE_URL}/#organization`,
  name: 'Zouq AI',
  alternateName: 'ذوق AI',
  url: SITE_URL,
  logo: {
    '@type': 'ImageObject',
    url: `${SITE_URL}/icon-512.png`,
    width: 512,
    height: 512,
  },
  description: 'منصة ذكاء اصطناعي شاملة لإدارة المطاعم والكافيهات والعربات المتنقلة في السعودية ودول الخليج. AI-powered restaurant management platform for Saudi Arabia and the GCC.',
  foundingDate: '2025',
  email: 'support@zouq.ai',
  areaServed: [
    { '@type': 'Country', name: 'Saudi Arabia' },
    { '@type': 'Country', name: 'United Arab Emirates' },
    { '@type': 'Country', name: 'Kuwait' },
    { '@type': 'Country', name: 'Bahrain' },
    { '@type': 'Country', name: 'Qatar' },
    { '@type': 'Country', name: 'Oman' },
  ],
  sameAs: [
    'https://twitter.com/ZouqAI',
    'https://www.linkedin.com/company/zouq-ai',
    'https://www.instagram.com/zouq.ai',
  ],
};

// SoftwareApplication Schema.org for the SaaS itself
export const SOFTWARE_APPLICATION_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  '@id': `${SITE_URL}/#software`,
  name: 'Zouq AI',
  alternateName: 'ذوق AI',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Restaurant Management Software',
  operatingSystem: 'Web, iOS, Android, Windows, macOS',
  url: SITE_URL,
  inLanguage: ['ar', 'en'],
  description: 'منصة شاملة بالذكاء الاصطناعي لإدارة المطاعم والكافيهات والعربات المتنقلة. نقطة بيع، شاشة مطبخ، فوترة زاتكا، وكيل صوتي يفهم العامية السعودية.',
  offers: [
    { '@type': 'Offer', name: 'Free Trial', price: '0', priceCurrency: 'SAR', description: '14 يوم تجربة مجانية' },
    { '@type': 'Offer', name: 'Basic', price: '99', priceCurrency: 'SAR', description: 'الباقة الأساسية - شهرياً' },
    { '@type': 'Offer', name: 'Pro', price: '249', priceCurrency: 'SAR', description: 'الباقة الاحترافية - شهرياً' },
    { '@type': 'Offer', name: 'Enterprise', price: '499', priceCurrency: 'SAR', description: 'باقة المؤسسات - شهرياً' },
  ],
  aggregateRating: {
    '@type': 'AggregateRating',
    ratingValue: '4.8',
    reviewCount: '127',
    bestRating: '5',
    worstRating: '1',
  },
  featureList: [
    'AI Voice Agent (Saudi dialect)',
    'POS System with Table Management',
    'Smart Kitchen Display (KDS)',
    'ZATCA-compliant E-Invoicing',
    'Delivery Fleet Management',
    'Smart Accounting & Analytics',
    'AI Website Builder',
    'Marketing AI Agent',
    'Multi-branch Support',
    'Arabic-first UI with RTL support',
  ],
  screenshot: `${SITE_URL}/og-image.png`,
  publisher: { '@id': `${SITE_URL}/#organization` },
};

// Helper: build BreadcrumbList Schema
export function buildBreadcrumb(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

// Helper: build FAQPage Schema from array of {q, a}
export function buildFAQSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };
}

// Helper: build Product Schema for vertical landing pages
export function buildProductSchema({ name, description, url, image, offers, rating }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name,
    description,
    url,
    image: image || `${SITE_URL}/og-image.png`,
    brand: { '@type': 'Brand', name: 'Zouq AI' },
    offers: offers || {
      '@type': 'AggregateOffer',
      priceCurrency: 'SAR',
      lowPrice: '0',
      highPrice: '499',
      offerCount: '4',
    },
    aggregateRating: rating || {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '127',
    },
  };
}
